import { Eye, HeartStraight, Pause, VideoCamera } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage, FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import styled, { css } from 'styled-components';

import { useAppSelector } from '@/redux/hooks';
import { getCatalogAbsenteeBidders, getCatalogHasVideo } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getLiveCatalogBidders } from '@/redux/modules/console';
import { getItemSummaryCatalogId } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getSavedItemCountNum } from '@/redux/modules/savedItemCount';

type Props = {
    catalogId?: number;
    className?: string;
    isPaused?: boolean;
    itemId?: number;
};

const ConsoleSocial = ({ catalogId, className = '', isPaused, itemId }: Props) => {
    const catalogIdFromItem = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const saved = useAppSelector((state) => getSavedItemCountNum(state, itemId));
    const hasVideo = useAppSelector((state) => getCatalogHasVideo(state, catalogId));
    const absenteeBidders = useAppSelector((state) => getCatalogAbsenteeBidders(state, catalogId || catalogIdFromItem));
    const bidders = useAppSelector((state) => getLiveCatalogBidders(state, catalogId || catalogIdFromItem));

    let savedCount = 0;
    if (!catalogIdFromItem && itemId) {
        savedCount = saved;
    }

    return (
        <ContainerDiv className={className}>
            <>
                {isPaused && (
                    <LiveContainerDiv $useTextOnConsoleLiveStyles={isPaused}>
                        <BodySecondary className="center flex">
                            {hasVideo && (
                                <VideoCamera
                                    className="mr-[6px]"
                                    color="primary"
                                    size="sm"
                                    style="fill"
                                />
                            )}
                            {isPaused && (
                                <Pause
                                    className="mr-[6px]"
                                    color="primary"
                                    size="sm"
                                    style="bold"
                                />
                            )}
                            <FormattedMessage id={isPaused ? 'paused' : 'live'} />
                        </BodySecondary>
                    </LiveContainerDiv>
                )}
                <EyeStylesText $useTextOnConsoleEyeStyles={!isPaused}>
                    <Eye
                        className="mr-[6px]"
                        color="primary"
                        size="sm"
                    />
                    <BodySecondary>
                        <FormattedNumber
                            key="console-social-bidders"
                            maximumFractionDigits={0}
                            minimumFractionDigits={0}
                            value={bidders + absenteeBidders || 1}
                        />
                    </BodySecondary>
                </EyeStylesText>
            </>
            {Boolean(savedCount) && (
                <HeartContainerDiv>
                    <HeartStraight
                        className="mr-[6px]"
                        color="primary"
                        size="sm"
                    />
                    <BodySecondary>
                        <FormattedNumber
                            key="console-social-saved-count"
                            maximumFractionDigits={0}
                            minimumFractionDigits={0}
                            value={savedCount}
                        />
                    </BodySecondary>
                </HeartContainerDiv>
            )}
        </ContainerDiv>
    );
};

export default React.memo<Props>(ConsoleSocial);

const ContainerDiv = styled.div`
    position: absolute;
    display: flex;
    align-items: flex-start;
    padding: 0;
    color: ${({ theme }) => theme.colors.black};
    top: 5px;
    margin: 5px 5px;
    left: 0;
    z-index: 2;
    flex-direction: column;
    user-select: none;

    & > div:first-child {
        margin-bottom: 5px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin: 8px 0 0 13px;
    }
`;

const ContainerStyles = css`
    border-radius: 2px;
    background-color: ${({ theme }) => theme.colors.shadowOverlay50};
    padding: 0 6px;
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.grey100};
    text-align: left;
`;

const TextOnConsoleLiveStyles = css`
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    padding-right: 0;
`;

const LiveContainerDiv = styled.div<{ $useTextOnConsoleLiveStyles: boolean }>`
    ${ContainerStyles}
    ${({ $useTextOnConsoleLiveStyles }) => $useTextOnConsoleLiveStyles && TextOnConsoleLiveStyles}
`;

const TextOnConsoleEyeStyles = css`
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
`;

const EyeStylesText = styled.div<{ $useTextOnConsoleEyeStyles: boolean }>`
    ${ContainerStyles}
    ${({ $useTextOnConsoleEyeStyles }) => $useTextOnConsoleEyeStyles && TextOnConsoleEyeStyles}
`;

const HeartContainerDiv = styled.div`
    ${ContainerStyles}
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
`;
